import { supabase } from "@/lib/supabase";
import { diffRecords, fullRowChanges, recordAudit } from "@/lib/audit";
import { useRealtimeRefresh } from "@/hooks/useRealtimeRefresh";
import { useRecentlyChangedRowsApi } from "@/contexts/RecentlyChangedRowsContext";
import { isE2EMode } from "@/lib/e2e";
import { useCallback, useEffect, useState } from "react";

export interface ActionItem {
  id: string;
  date: string | null;
  client: string | null;
  task: string;
  owner: string | null;
  due_date: string | null;
  completed: boolean;
  notes: string;
  archived: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function useActionItemsData() {
  const [items, setItems] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { markSelfEdit } = useRecentlyChangedRowsApi();

  const loadItems = useCallback(async () => {
    setError(null);
    const { data, error: fetchError } = await supabase
      .from("action_items")
      .select("*")
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true });

    if (fetchError) {
      setError(fetchError.message);
      setLoading(false);
      return;
    }

    setItems(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // CAP2-207's browser test needs to exercise the refetch behavior without
  // depending on Supabase websocket delivery, which is nondeterministic in CI.
  // This invokes the exact callback used by useRealtimeRefresh and is never
  // installed outside E2E mode.
  useEffect(() => {
    if (!isE2EMode() || typeof window === "undefined") return;
    const handleE2EReload = () => { void loadItems(); };
    window.addEventListener("e2e:reload-action-items", handleE2EReload);
    return () => window.removeEventListener("e2e:reload-action-items", handleE2EReload);
  }, [loadItems]);

  // Reflect edits from other users/tabs live. Our own writes are marked via
  // markSelfEdit in each mutation below, so skipRefetchOnSelfEdit avoids a
  // redundant refetch (and row remount) on the echo of our own optimistic edits.
  useRealtimeRefresh(["action_items"], loadItems, { skipRefetchOnSelfEdit: true });

  const createItem = useCallback(async (): Promise<ActionItem | null> => {
    setError(null);
    const { data, error: insertError } = await supabase
      .from("action_items")
      .insert({ task: "", date: localToday(), sort_order: items.length })
      .select()
      .single();

    if (insertError) {
      setError(insertError.message);
      return null;
    }

    recordAudit({
      table: "action_items",
      recordId: data.id,
      operation: "INSERT",
      changes: fullRowChanges(data, "INSERT"),
      recordLabel: data.task ?? null,
    });

    markSelfEdit("action_items", data.id);
    setItems((prev) => [...prev, data]);
    return data;
  }, [items.length, markSelfEdit]);

  const updateItem = useCallback(async (
    id: string,
    updates: Partial<Omit<ActionItem, "id" | "created_at" | "updated_at">>
  ) => {
    setError(null);
    const oldItem = items.find((item) => item.id === id);
    // Optimistic update — revert on error
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updates } : item))
    );

    markSelfEdit("action_items", id);
    const { error: updateError } = await supabase
      .from("action_items")
      .update(updates)
      .eq("id", id);

    if (updateError) {
      setError(updateError.message);
      // Revert by reloading from server
      loadItems();
      return;
    }

    if (oldItem) {
      const oldSubset = Object.fromEntries(
        (Object.keys(updates) as (keyof ActionItem)[]).map((key) => [key, oldItem[key]])
      );
      recordAudit({
        table: "action_items",
        recordId: id,
        operation: "UPDATE",
        changes: diffRecords(oldSubset, updates),
        recordLabel: oldItem.task ?? null,
      });
    }
  }, [loadItems, items, markSelfEdit]);

  const deleteItem = useCallback(async (id: string) => {
    setError(null);
    const oldItem = items.find((item) => item.id === id);
    markSelfEdit("action_items", id);
    const { error: deleteError } = await supabase
      .from("action_items")
      .delete()
      .eq("id", id);

    if (deleteError) {
      setError(deleteError.message);
      return;
    }

    if (oldItem) {
      recordAudit({
        table: "action_items",
        recordId: id,
        operation: "DELETE",
        changes: fullRowChanges(oldItem as unknown as Record<string, unknown>, "DELETE"),
        recordLabel: oldItem.task ?? null,
      });
    }

    setItems((prev) => prev.filter((item) => item.id !== id));
  }, [items, markSelfEdit]);

  const archiveItems = useCallback(async (ids: string[]) => {
    setError(null);
    setItems((prev) =>
      prev.map((item) => (ids.includes(item.id) ? { ...item, archived: true } : item))
    );

    for (const id of ids) markSelfEdit("action_items", id);
    const { error: updateError } = await supabase
      .from("action_items")
      .update({ archived: true })
      .in("id", ids);

    if (updateError) {
      setError(updateError.message);
      loadItems();
      return;
    }

    for (const id of ids) {
      const item = items.find((i) => i.id === id);
      recordAudit({
        table: "action_items",
        recordId: id,
        operation: "UPDATE",
        changes: { archived: { old: false, new: true } },
        recordLabel: item?.task ?? null,
      });
    }
  }, [loadItems, items, markSelfEdit]);

  const createItemAfter = useCallback(async (afterId: string): Promise<ActionItem | null> => {
    setError(null);
    const { data, error: insertError } = await supabase
      .from("action_items")
      .insert({ task: "", date: localToday(), sort_order: 9999 })
      .select()
      .single();

    if (insertError || !data) {
      if (insertError) setError(insertError.message);
      return null;
    }

    const newItem = data as unknown as ActionItem;

    recordAudit({
      table: "action_items",
      recordId: newItem.id,
      operation: "INSERT",
      changes: fullRowChanges(data as unknown as Record<string, unknown>, "INSERT"),
      recordLabel: newItem.task ?? null,
    });

    markSelfEdit("action_items", newItem.id);

    const activeItems = items
      .filter((i) => !i.archived)
      .sort((a, b) => a.sort_order - b.sort_order);

    const afterIndex = activeItems.findIndex((i) => i.id === afterId);
    const insertAt = afterIndex + 1;

    const newActiveItems = [
      ...activeItems.slice(0, insertAt),
      newItem,
      ...activeItems.slice(insertAt),
    ].map((item, index): ActionItem => ({ ...item, sort_order: index }));

    setItems((prev) => {
      const activeIds = new Set(newActiveItems.map((i) => i.id));
      return [
        ...newActiveItems,
        ...prev.filter((i) => !activeIds.has(i.id)),
      ];
    });

    for (const item of newActiveItems) markSelfEdit("action_items", item.id);
    await Promise.all(
      newActiveItems.map((item, index) =>
        supabase.from("action_items").update({ sort_order: index }).eq("id", item.id)
      )
    );

    return newItem;
  }, [items, markSelfEdit]);

  const reorderItems = useCallback(async (reorderedActiveItems: ActionItem[]) => {
    const activeIds = new Set(reorderedActiveItems.map((i) => i.id));
    setItems((prev) => [
      ...reorderedActiveItems.map((item, index): ActionItem => ({ ...item, sort_order: index })),
      ...prev.filter((i) => !activeIds.has(i.id)),
    ]);

    for (const item of reorderedActiveItems) markSelfEdit("action_items", item.id);
    await Promise.all(
      reorderedActiveItems.map((item, index) =>
        supabase.from("action_items").update({ sort_order: index }).eq("id", item.id)
      )
    );
  }, [markSelfEdit]);

  return { items, loading, error, createItem, createItemAfter, updateItem, deleteItem, archiveItems, reorderItems };
}
